"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.lucene = void 0;
var lucene;
(function (lucene) {
    /**
    * Parser for Lucene Syntax
    */
    class LuceneParser {
        //#endregion MEMBER VARIABLES
        //#region MEMBER FUNCTIONS
        static parse(input, startRule = StartRule.Start) {
            var parseFunc = LuceneParser.getParseFunc(startRule);
            LuceneParser.pos = 0;
            LuceneParser.reportFailures = 0;
            LuceneParser.rightmostFailuresPos = 0;
            LuceneParser.rightmostFailuresExpected = [];
            LuceneParser.input = input;
            var result = parseFunc();
            /*
             * The parser is now in one of the following three states:
             *
             * 1. The parser successfully parsed the whole input.
             *
             *    - |result !== null|
             *    - |pos === input.length|
             *    - |rightmostFailuresExpected| may or may not contain something
             *
             * 2. The parser successfully parsed only a part of the input.
             *
             *    - |result !== null|
             *    - |pos < input.length|
             *    - |rightmostFailuresExpected| may or may not contain something
             *
             * 3. The parser did not successfully parse any part of the input.
             *
             *   - |result === null|
             *   - |pos === 0|
             *   - |rightmostFailuresExpected| contains at least one failure
             *
             * All code following this comment (including called functions) must
             * handle these states.
             */
            if (result === null || LuceneParser.pos !== input.length) {
                var offset = Math.max(LuceneParser.pos, LuceneParser.rightmostFailuresPos);
                var found = offset < input.length ? input.charAt(offset) : null;
                var errorPosition = LuceneParser.computeErrorPosition();
                throw new LuceneParserSyntaxError(LuceneParser.cleanupExpected(LuceneParser.rightmostFailuresExpected), found, offset, errorPosition.line, errorPosition.column);
            }
            return result;
        }
        static quote(s) {
            /*
            * ECMA-262, 5th ed., 7.8.4: All characters may appear literally in a
            * string literal except for the closing quote character, backslash,
            * carriage return, line separator, paragraph separator, and line feed.
            * Any character may appear in the form of an escape sequence.
            *
            * For portability, we also escape escape all control and non-ASCII
            * characters. Note that "\0" and "\v" escape sequences are not used
            * because JSHint does not like the first and IE the second.
            */
            return '"' + s
                .replace(/\\/g, '\\\\') // backslash
                .replace(/"/g, '\\"') // closing quote character
                .replace(/\x08/g, '\\b') // backspace
                .replace(/\t/g, '\\t') // horizontal tab
                .replace(/\n/g, '\\n') // line feed
                .replace(/\f/g, '\\f') // form feed
                .replace(/\r/g, '\\r') // carriage return
                .replace(/[\x00-\x07\x0B\x0E-\x1F\x80-\uFFFF]/g, LuceneParser.escape)
                + '"';
        }
        //#region Private
        static getParseFunc(startRule) {
            switch (startRule) {
                case StartRule.Start:
                    return LuceneParser.parse_start;
                case StartRule.Node:
                    return LuceneParser.parse_node;
                case StartRule.GroupExp:
                    return LuceneParser.parse_group_exp;
                case StartRule.ParenExp:
                    return LuceneParser.parse_paren_exp;
                case StartRule.FieldExp:
                    return LuceneParser.parse_field_exp;
                case StartRule.FieldName:
                    return LuceneParser.parse_fieldname;
                case StartRule.Term:
                    return LuceneParser.parse_term;
                case StartRule.UnquotedTerm:
                    return LuceneParser.parse_unquoted_term;
                case StartRule.TermChar:
                    return LuceneParser.parse_term_char;
                case StartRule.QuotedTerm:
                    return LuceneParser.parse_quoted_term;
                case StartRule.ProximityModifier:
                    return LuceneParser.parse_proximity_modifier;
                case StartRule.BoostModifier:
                    return LuceneParser.parse_boost_modifier;
                case StartRule.FuzzyModifier:
                    return LuceneParser.parse_fuzzy_modifier;
                case StartRule.DecimalOrIntExp:
                    return LuceneParser.parse_decimal_or_int_exp;
                case StartRule.DecimalExp:
                    return LuceneParser.parse_decimal_exp;
                case StartRule.IntExp:
                    return LuceneParser.parse_int_exp;
                case StartRule.RangeOperatorExp:
                    return LuceneParser.parse_range_operator_exp;
                case StartRule.OperatorExp:
                    return LuceneParser.parse_operator_exp;
                case StartRule.Operator:
                    return LuceneParser.parse_operator;
                case StartRule.PrefixOperatorExp:
                    return LuceneParser.parse_prefix_operator_exp;
                case StartRule.PrefixOperator:
                    return LuceneParser.parse_prefix_operator;
                case StartRule._:
                    return LuceneParser.parse__;
                case StartRule.EOF:
                    return LuceneParser.parse_EOF;
                default: return null;
            }
        }
        static matchFailed(failure) {
            if (LuceneParser.pos < LuceneParser.rightmostFailuresPos) {
                return;
            }
            if (LuceneParser.pos > LuceneParser.rightmostFailuresPos) {
                LuceneParser.rightmostFailuresPos = LuceneParser.pos;
                LuceneParser.rightmostFailuresExpected = [];
            }
            LuceneParser.rightmostFailuresExpected.push(failure);
        }
        static parse_start() {
            var result0, result1, result2;
            var pos0, pos1;
            pos0 = LuceneParser.pos;
            pos1 = LuceneParser.pos;
            result0 = [];
            result1 = LuceneParser.parse__();
            while (result1 !== null) {
                result0.push(result1);
                result1 = LuceneParser.parse__();
            }
            if (result0 !== null) {
                result2 = LuceneParser.parse_node();
                if (result2 !== null) {
                    result1 = [];
                    while (result2 !== null) {
                        result1.push(result2);
                        result2 = LuceneParser.parse_node();
                    }
                }
                else {
                    result1 = null;
                }
                if (result1 !== null) {
                    result0 = [result0, result1];
                }
                else {
                    result0 = null;
                    LuceneParser.pos = pos1;
                }
            }
            else {
                result0 = null;
                LuceneParser.pos = pos1;
            }
            if (result0 !== null) {
                result0 = (function (offset, node) {
                    return node[0];
                })(pos0, result0[1]);
            }
            if (result0 === null) {
                LuceneParser.pos = pos0;
            }
            if (result0 === null) {
                pos0 = LuceneParser.pos;
                result0 = [];
                result1 = LuceneParser.parse__();
                while (result1 !== null) {
                    result0.push(result1);
                    result1 = LuceneParser.parse__();
                }
                if (result0 !== null) {
                    result0 = (function (offset) {
                        return {};
                    })(pos0);
                }
                if (result0 === null) {
                    LuceneParser.pos = pos0;
                }
                if (result0 === null) {
                    pos0 = LuceneParser.pos;
                    result0 = LuceneParser.parse_EOF();
                    if (result0 !== null) {
                        result0 = (function (offset) {
                            return {};
                        })(pos0);
                    }
                    if (result0 === null) {
                        LuceneParser.pos = pos0;
                    }
                }
            }
            return result0;
        }
        static parse_node() {
            var result0, result1, result2, result3;
            var pos0, pos1;
            pos0 = LuceneParser.pos;
            pos1 = LuceneParser.pos;
            result0 = LuceneParser.parse_operator_exp();
            if (result0 !== null) {
                result1 = LuceneParser.parse_EOF();
                if (result1 !== null) {
                    result0 = [result0, result1];
                }
                else {
                    result0 = null;
                    LuceneParser.pos = pos1;
                }
            }
            else {
                result0 = null;
                LuceneParser.pos = pos1;
            }
            if (result0 !== null) {
                result0 = (function (offset, operator) {
                    return {
                        'operator': operator
                    };
                })(pos0, result0[0]);
            }
            if (result0 === null) {
                LuceneParser.pos = pos0;
            }
            if (result0 === null) {
                pos0 = LuceneParser.pos;
                pos1 = LuceneParser.pos;
                result0 = LuceneParser.parse_operator_exp();
                if (result0 !== null) {
                    result1 = LuceneParser.parse_node();
                    if (result1 !== null) {
                        result0 = [result0, result1];
                    }
                    else {
                        result0 = null;
                        LuceneParser.pos = pos1;
                    }
                }
                else {
                    result0 = null;
                    LuceneParser.pos = pos1;
                }
                if (result0 !== null) {
                    result0 = (function (offset, operator, right) {
                        return right;
                    })(pos0, result0[0], result0[1]);
                }
                if (result0 === null) {
                    LuceneParser.pos = pos0;
                }
                if (result0 === null) {
                    pos0 = LuceneParser.pos;
                    pos1 = LuceneParser.pos;
                    result0 = LuceneParser.parse_group_exp();
                    if (result0 !== null) {
                        result1 = [];
                        result2 = LuceneParser.parse_operator_exp();
                        while (result2 !== null) {
                            result1.push(result2);
                            result2 = LuceneParser.parse_operator_exp();
                        }
                        if (result1 !== null) {
                            result2 = [];
                            result3 = LuceneParser.parse_node();
                            while (result3 !== null) {
                                result2.push(result3);
                                result3 = LuceneParser.parse_node();
                            }
                            if (result2 !== null) {
                                result0 = [result0, result1, result2];
                            }
                            else {
                                result0 = null;
                                LuceneParser.pos = pos1;
                            }
                        }
                        else {
                            result0 = null;
                            LuceneParser.pos = pos1;
                        }
                    }
                    else {
                        result0 = null;
                        LuceneParser.pos = pos1;
                    }
                    if (result0 !== null) {
                        result0 = (function (offset, left, operator, right) {
                            var node = {
                                'left': left
                            };
                            var right = right.length == 0
                                ? null
                                : right[0]['right'] == null
                                    ? right[0]['left']
                                    : right[0];
                            if (right != null) {
                                node['operator'] = operator == '' ? '<implicit>' : operator[0];
                                node['right'] = right;
                            }
                            return node;
                        })(pos0, result0[0], result0[1], result0[2]);
                    }
                    if (result0 === null) {
                        LuceneParser.pos = pos0;
                    }
                }
            }
            return result0;
        }
        static parse_group_exp() {
            var result0, result1, result2;
            var pos0, pos1;
            pos0 = LuceneParser.pos;
            pos1 = LuceneParser.pos;
            result0 = LuceneParser.parse_field_exp();
            if (result0 !== null) {
                result1 = [];
                result2 = LuceneParser.parse__();
                while (result2 !== null) {
                    result1.push(result2);
                    result2 = LuceneParser.parse__();
                }
                if (result1 !== null) {
                    result0 = [result0, result1];
                }
                else {
                    result0 = null;
                    LuceneParser.pos = pos1;
                }
            }
            else {
                result0 = null;
                LuceneParser.pos = pos1;
            }
            if (result0 !== null) {
                result0 = (function (offset, field_exp) {
                    return field_exp;
                })(pos0, result0[0]);
            }
            if (result0 === null) {
                LuceneParser.pos = pos0;
            }
            if (result0 === null) {
                result0 = LuceneParser.parse_paren_exp();
            }
            return result0;
        }
        static parse_paren_exp() {
            var result0, result1, result2, result3, result4;
            var pos0, pos1;
            pos0 = LuceneParser.pos;
            pos1 = LuceneParser.pos;
            if (LuceneParser.input.charCodeAt(LuceneParser.pos) === 40) {
                result0 = "(";
                LuceneParser.pos++;
            }
            else {
                result0 = null;
                if (LuceneParser.reportFailures === 0) {
                    LuceneParser.matchFailed("\"(\"");
                }
            }
            if (result0 !== null) {
                result2 = LuceneParser.parse_node();
                if (result2 !== null) {
                    result1 = [];
                    while (result2 !== null) {
                        result1.push(result2);
                        result2 = LuceneParser.parse_node();
                    }
                }
                else {
                    result1 = null;
                }
                if (result1 !== null) {
                    if (LuceneParser.input.charCodeAt(LuceneParser.pos) === 41) {
                        result2 = ")";
                        LuceneParser.pos++;
                    }
                    else {
                        result2 = null;
                        if (LuceneParser.reportFailures === 0) {
                            LuceneParser.matchFailed("\")\"");
                        }
                    }
                    if (result2 !== null) {
                        result3 = [];
                        result4 = LuceneParser.parse__();
                        while (result4 !== null) {
                            result3.push(result4);
                            result4 = LuceneParser.parse__();
                        }
                        if (result3 !== null) {
                            result0 = [result0, result1, result2, result3];
                        }
                        else {
                            result0 = null;
                            LuceneParser.pos = pos1;
                        }
                    }
                    else {
                        result0 = null;
                        LuceneParser.pos = pos1;
                    }
                }
                else {
                    result0 = null;
                    LuceneParser.pos = pos1;
                }
            }
            else {
                result0 = null;
                LuceneParser.pos = pos1;
            }
            if (result0 !== null) {
                result0 = (function (offset, node) {
                    return node[0];
                })(pos0, result0[1]);
            }
            if (result0 === null) {
                LuceneParser.pos = pos0;
            }
            return result0;
        }
        static parse_field_exp() {
            var result0, result1;
            var pos0, pos1;
            pos0 = LuceneParser.pos;
            pos1 = LuceneParser.pos;
            result0 = LuceneParser.parse_fieldname();
            result0 = result0 !== null ? result0 : "";
            if (result0 !== null) {
                result1 = LuceneParser.parse_range_operator_exp();
                if (result1 !== null) {
                    result0 = [result0, result1];
                }
                else {
                    result0 = null;
                    LuceneParser.pos = pos1;
                }
            }
            else {
                result0 = null;
                LuceneParser.pos = pos1;
            }
            if (result0 !== null) {
                result0 = (function (offset, fieldname, range) {
                    range['field'] =
                        fieldname == ''
                            ? "<implicit>"
                            : fieldname;
                    return range;
                })(pos0, result0[0], result0[1]);
            }
            if (result0 === null) {
                LuceneParser.pos = pos0;
            }
            if (result0 === null) {
                pos0 = LuceneParser.pos;
                pos1 = LuceneParser.pos;
                result0 = LuceneParser.parse_fieldname();
                if (result0 !== null) {
                    result1 = LuceneParser.parse_paren_exp();
                    if (result1 !== null) {
                        result0 = [result0, result1];
                    }
                    else {
                        result0 = null;
                        LuceneParser.pos = pos1;
                    }
                }
                else {
                    result0 = null;
                    LuceneParser.pos = pos1;
                }
                if (result0 !== null) {
                    result0 = (function (offset, fieldname, node) {
                        node['field'] = fieldname;
                        return node;
                    })(pos0, result0[0], result0[1]);
                }
                if (result0 === null) {
                    LuceneParser.pos = pos0;
                }
                if (result0 === null) {
                    pos0 = LuceneParser.pos;
                    pos1 = LuceneParser.pos;
                    result0 = LuceneParser.parse_fieldname();
                    result0 = result0 !== null ? result0 : "";
                    if (result0 !== null) {
                        result1 = LuceneParser.parse_term();
                        if (result1 !== null) {
                            result0 = [result0, result1];
                        }
                        else {
                            result0 = null;
                            LuceneParser.pos = pos1;
                        }
                    }
                    else {
                        result0 = null;
                        LuceneParser.pos = pos1;
                    }
                    if (result0 !== null) {
                        result0 = (function (offset, fieldname, term) {
                            var fieldexp = {
                                'field': fieldname == ''
                                    ? "<implicit>"
                                    : fieldname
                            };
                            for (var key in term)
                                fieldexp[key] = term[key];
                            return fieldexp;
                        })(pos0, result0[0], result0[1]);
                    }
                    if (result0 === null) {
                        LuceneParser.pos = pos0;
                    }
                }
            }
            return result0;
        }
        static parse_fieldname() {
            var result0, result1;
            var pos0, pos1;
            pos0 = LuceneParser.pos;
            pos1 = LuceneParser.pos;
            result0 = LuceneParser.parse_unquoted_term();
            if (result0 !== null) {
                if (/^[:]/.test(LuceneParser.input.charAt(LuceneParser.pos))) {
                    result1 = LuceneParser.input.charAt(LuceneParser.pos);
                    LuceneParser.pos++;
                }
                else {
                    result1 = null;
                    if (LuceneParser.reportFailures === 0) {
                        LuceneParser.matchFailed("[:]");
                    }
                }
                if (result1 !== null) {
                    result0 = [result0, result1];
                }
                else {
                    result0 = null;
                    LuceneParser.pos = pos1;
                }
            }
            else {
                result0 = null;
                LuceneParser.pos = pos1;
            }
            if (result0 !== null) {
                result0 = (function (offset, fieldname) {
                    return fieldname;
                })(pos0, result0[0]);
            }
            if (result0 === null) {
                LuceneParser.pos = pos0;
            }
            return result0;
        }
        static parse_term() {
            var result0, result1, result2, result3, result4, result5;
            var pos0, pos1;
            pos0 = LuceneParser.pos;
            pos1 = LuceneParser.pos;
            result0 = LuceneParser.parse_prefix_operator_exp();
            result0 = result0 !== null ? result0 : "";
            if (result0 !== null) {
                result1 = LuceneParser.parse_quoted_term();
                if (result1 !== null) {
                    result2 = LuceneParser.parse_proximity_modifier();
                    result2 = result2 !== null ? result2 : "";
                    if (result2 !== null) {
                        result3 = LuceneParser.parse_boost_modifier();
                        result3 = result3 !== null ? result3 : "";
                        if (result3 !== null) {
                            result4 = [];
                            result5 = LuceneParser.parse__();
                            while (result5 !== null) {
                                result4.push(result5);
                                result5 = LuceneParser.parse__();
                            }
                            if (result4 !== null) {
                                result0 = [result0, result1, result2, result3, result4];
                            }
                            else {
                                result0 = null;
                                LuceneParser.pos = pos1;
                            }
                        }
                        else {
                            result0 = null;
                            LuceneParser.pos = pos1;
                        }
                    }
                    else {
                        result0 = null;
                        LuceneParser.pos = pos1;
                    }
                }
                else {
                    result0 = null;
                    LuceneParser.pos = pos1;
                }
            }
            else {
                result0 = null;
                LuceneParser.pos = pos1;
            }
            if (result0 !== null) {
                result0 = (function (offset, op, term, proximity, boost) {
                    var result = { 'term': term };
                    if ('' != proximity) {
                        result['proximity'] = proximity;
                    }
                    if ('' != boost) {
                        result['boost'] = boost;
                    }
                    if ('' != op) {
                        result['prefix'] = op;
                    }
                    return result;
                })(pos0, result0[0], result0[1], result0[2], result0[3]);
            }
            if (result0 === null) {
                LuceneParser.pos = pos0;
            }
            if (result0 === null) {
                pos0 = LuceneParser.pos;
                pos1 = LuceneParser.pos;
                result0 = LuceneParser.parse_prefix_operator_exp();
                result0 = result0 !== null ? result0 : "";
                if (result0 !== null) {
                    result1 = LuceneParser.parse_unquoted_term();
                    if (result1 !== null) {
                        result2 = LuceneParser.parse_fuzzy_modifier();
                        result2 = result2 !== null ? result2 : "";
                        if (result2 !== null) {
                            result3 = LuceneParser.parse_boost_modifier();
                            result3 = result3 !== null ? result3 : "";
                            if (result3 !== null) {
                                result4 = [];
                                result5 = LuceneParser.parse__();
                                while (result5 !== null) {
                                    result4.push(result5);
                                    result5 = LuceneParser.parse__();
                                }
                                if (result4 !== null) {
                                    result0 = [result0, result1, result2, result3, result4];
                                }
                                else {
                                    result0 = null;
                                    LuceneParser.pos = pos1;
                                }
                            }
                            else {
                                result0 = null;
                                LuceneParser.pos = pos1;
                            }
                        }
                        else {
                            result0 = null;
                            LuceneParser.pos = pos1;
                        }
                    }
                    else {
                        result0 = null;
                        LuceneParser.pos = pos1;
                    }
                }
                else {
                    result0 = null;
                    LuceneParser.pos = pos1;
                }
                if (result0 !== null) {
                    result0 = (function (offset, op, term, similarity, boost) {
                        var result = { 'term': term };
                        if ('' != similarity) {
                            result['similarity'] = similarity;
                        }
                        if ('' != boost) {
                            result['boost'] = boost;
                        }
                        if ('' != op) {
                            result['prefix'] = op;
                        }
                        return result;
                    })(pos0, result0[0], result0[1], result0[2], result0[3]);
                }
                if (result0 === null) {
                    LuceneParser.pos = pos0;
                }
            }
            return result0;
        }
        static parse_unquoted_term() {
            var result0, result1;
            var pos0;
            pos0 = LuceneParser.pos;
            result1 = LuceneParser.parse_term_char();
            if (result1 !== null) {
                result0 = [];
                while (result1 !== null) {
                    result0.push(result1);
                    result1 = LuceneParser.parse_term_char();
                }
            }
            else {
                result0 = null;
            }
            if (result0 !== null) {
                result0 = (function (offset, term) {
                    return term.join('');
                })(pos0, result0);
            }
            if (result0 === null) {
                LuceneParser.pos = pos0;
            }
            return result0;
        }
        static parse_term_char() {
            var result0;
            if (LuceneParser.input.charCodeAt(LuceneParser.pos) === 46) {
                result0 = ".";
                LuceneParser.pos++;
            }
            else {
                result0 = null;
                if (LuceneParser.reportFailures === 0) {
                    LuceneParser.matchFailed("\".\"");
                }
            }
            if (result0 === null) {
                if (/^[^: \t\r\n\f{}()"+-\/^~[\]]/.test(LuceneParser.input.charAt(LuceneParser.pos))) {
                    result0 = LuceneParser.input.charAt(LuceneParser.pos);
                    LuceneParser.pos++;
                }
                else {
                    result0 = null;
                    if (LuceneParser.reportFailures === 0) {
                        LuceneParser.matchFailed("[^: \\t\\r\\n\\f{}()\"+-\\/^~[\\]]");
                    }
                }
            }
            return result0;
        }
        static parse_quoted_term() {
            var result0, result1, result2;
            var pos0, pos1;
            pos0 = LuceneParser.pos;
            pos1 = LuceneParser.pos;
            if (LuceneParser.input.charCodeAt(LuceneParser.pos) === 34) {
                result0 = "\"";
                LuceneParser.pos++;
            }
            else {
                result0 = null;
                if (LuceneParser.reportFailures === 0) {
                    LuceneParser.matchFailed("\"\\\"\"");
                }
            }
            if (result0 !== null) {
                if (/^[^"]/.test(LuceneParser.input.charAt(LuceneParser.pos))) {
                    result2 = LuceneParser.input.charAt(LuceneParser.pos);
                    LuceneParser.pos++;
                }
                else {
                    result2 = null;
                    if (LuceneParser.reportFailures === 0) {
                        LuceneParser.matchFailed("[^\"]");
                    }
                }
                if (result2 !== null) {
                    result1 = [];
                    while (result2 !== null) {
                        result1.push(result2);
                        if (/^[^"]/.test(LuceneParser.input.charAt(LuceneParser.pos))) {
                            result2 = LuceneParser.input.charAt(LuceneParser.pos);
                            LuceneParser.pos++;
                        }
                        else {
                            result2 = null;
                            if (LuceneParser.reportFailures === 0) {
                                LuceneParser.matchFailed("[^\"]");
                            }
                        }
                    }
                }
                else {
                    result1 = null;
                }
                if (result1 !== null) {
                    if (LuceneParser.input.charCodeAt(LuceneParser.pos) === 34) {
                        result2 = "\"";
                        LuceneParser.pos++;
                    }
                    else {
                        result2 = null;
                        if (LuceneParser.reportFailures === 0) {
                            LuceneParser.matchFailed("\"\\\"\"");
                        }
                    }
                    if (result2 !== null) {
                        result0 = [result0, result1, result2];
                    }
                    else {
                        result0 = null;
                        LuceneParser.pos = pos1;
                    }
                }
                else {
                    result0 = null;
                    LuceneParser.pos = pos1;
                }
            }
            else {
                result0 = null;
                LuceneParser.pos = pos1;
            }
            if (result0 !== null) {
                result0 = (function (offset, term) {
                    return term.join('');
                })(pos0, result0[1]);
            }
            if (result0 === null) {
                LuceneParser.pos = pos0;
            }
            return result0;
        }
        static parse_proximity_modifier() {
            var result0, result1;
            var pos0, pos1;
            pos0 = LuceneParser.pos;
            pos1 = LuceneParser.pos;
            if (LuceneParser.input.charCodeAt(LuceneParser.pos) === 126) {
                result0 = "~";
                LuceneParser.pos++;
            }
            else {
                result0 = null;
                if (LuceneParser.reportFailures === 0) {
                    LuceneParser.matchFailed("\"~\"");
                }
            }
            if (result0 !== null) {
                result1 = LuceneParser.parse_int_exp();
                if (result1 !== null) {
                    result0 = [result0, result1];
                }
                else {
                    result0 = null;
                    LuceneParser.pos = pos1;
                }
            }
            else {
                result0 = null;
                LuceneParser.pos = pos1;
            }
            if (result0 !== null) {
                result0 = (function (offset, proximity) {
                    return proximity;
                })(pos0, result0[1]);
            }
            if (result0 === null) {
                LuceneParser.pos = pos0;
            }
            return result0;
        }
        static parse_boost_modifier() {
            var result0, result1;
            var pos0, pos1;
            pos0 = LuceneParser.pos;
            pos1 = LuceneParser.pos;
            if (LuceneParser.input.charCodeAt(LuceneParser.pos) === 94) {
                result0 = "^";
                LuceneParser.pos++;
            }
            else {
                result0 = null;
                if (LuceneParser.reportFailures === 0) {
                    LuceneParser.matchFailed("\"^\"");
                }
            }
            if (result0 !== null) {
                result1 = LuceneParser.parse_decimal_or_int_exp();
                if (result1 !== null) {
                    result0 = [result0, result1];
                }
                else {
                    result0 = null;
                    LuceneParser.pos = pos1;
                }
            }
            else {
                result0 = null;
                LuceneParser.pos = pos1;
            }
            if (result0 !== null) {
                result0 = (function (offset, boost) {
                    return boost;
                })(pos0, result0[1]);
            }
            if (result0 === null) {
                LuceneParser.pos = pos0;
            }
            return result0;
        }
        static parse_fuzzy_modifier() {
            var result0, result1;
            var pos0, pos1;
            pos0 = LuceneParser.pos;
            pos1 = LuceneParser.pos;
            if (LuceneParser.input.charCodeAt(LuceneParser.pos) === 126) {
                result0 = "~";
                LuceneParser.pos++;
            }
            else {
                result0 = null;
                if (LuceneParser.reportFailures === 0) {
                    LuceneParser.matchFailed("\"~\"");
                }
            }
            if (result0 !== null) {
                result1 = LuceneParser.parse_decimal_exp();
                result1 = result1 !== null ? result1 : "";
                if (result1 !== null) {
                    result0 = [result0, result1];
                }
                else {
                    result0 = null;
                    LuceneParser.pos = pos1;
                }
            }
            else {
                result0 = null;
                LuceneParser.pos = pos1;
            }
            if (result0 !== null) {
                result0 = (function (offset, fuzziness) {
                    return fuzziness == '' ? 0.5 : fuzziness;
                })(pos0, result0[1]);
            }
            if (result0 === null) {
                LuceneParser.pos = pos0;
            }
            return result0;
        }
        static parse_decimal_or_int_exp() {
            var result0;
            result0 = LuceneParser.parse_decimal_exp();
            if (result0 === null) {
                result0 = LuceneParser.parse_int_exp();
            }
            return result0;
        }
        static parse_decimal_exp() {
            var result0, result1, result2;
            var pos0, pos1;
            pos0 = LuceneParser.pos;
            pos1 = LuceneParser.pos;
            if (LuceneParser.input.substr(LuceneParser.pos, 2) === "0.") {
                result0 = "0.";
                LuceneParser.pos += 2;
            }
            else {
                result0 = null;
                if (LuceneParser.reportFailures === 0) {
                    LuceneParser.matchFailed("\"0.\"");
                }
            }
            if (result0 !== null) {
                if (/^[0-9]/.test(LuceneParser.input.charAt(LuceneParser.pos))) {
                    result2 = LuceneParser.input.charAt(LuceneParser.pos);
                    LuceneParser.pos++;
                }
                else {
                    result2 = null;
                    if (LuceneParser.reportFailures === 0) {
                        LuceneParser.matchFailed("[0-9]");
                    }
                }
                if (result2 !== null) {
                    result1 = [];
                    while (result2 !== null) {
                        result1.push(result2);
                        if (/^[0-9]/.test(LuceneParser.input.charAt(LuceneParser.pos))) {
                            result2 = LuceneParser.input.charAt(LuceneParser.pos);
                            LuceneParser.pos++;
                        }
                        else {
                            result2 = null;
                            if (LuceneParser.reportFailures === 0) {
                                LuceneParser.matchFailed("[0-9]");
                            }
                        }
                    }
                }
                else {
                    result1 = null;
                }
                if (result1 !== null) {
                    result0 = [result0, result1];
                }
                else {
                    result0 = null;
                    LuceneParser.pos = pos1;
                }
            }
            else {
                result0 = null;
                LuceneParser.pos = pos1;
            }
            if (result0 !== null) {
                result0 = (function (offset, val) {
                    return parseFloat("0." + val.join(''));
                })(pos0, result0[1]);
            }
            if (result0 === null) {
                LuceneParser.pos = pos0;
            }
            return result0;
        }
        static parse_int_exp() {
            var result0, result1;
            var pos0;
            pos0 = LuceneParser.pos;
            if (/^[0-9]/.test(LuceneParser.input.charAt(LuceneParser.pos))) {
                result1 = LuceneParser.input.charAt(LuceneParser.pos);
                LuceneParser.pos++;
            }
            else {
                result1 = null;
                if (LuceneParser.reportFailures === 0) {
                    LuceneParser.matchFailed("[0-9]");
                }
            }
            if (result1 !== null) {
                result0 = [];
                while (result1 !== null) {
                    result0.push(result1);
                    if (/^[0-9]/.test(LuceneParser.input.charAt(LuceneParser.pos))) {
                        result1 = LuceneParser.input.charAt(LuceneParser.pos);
                        LuceneParser.pos++;
                    }
                    else {
                        result1 = null;
                        if (LuceneParser.reportFailures === 0) {
                            LuceneParser.matchFailed("[0-9]");
                        }
                    }
                }
            }
            else {
                result0 = null;
            }
            if (result0 !== null) {
                result0 = (function (offset, val) {
                    return parseInt(val.join(''));
                })(pos0, result0);
            }
            if (result0 === null) {
                LuceneParser.pos = pos0;
            }
            return result0;
        }
        static parse_range_operator_exp() {
            var result0, result1, result2, result3, result4, result5, result6;
            var pos0, pos1;
            pos0 = LuceneParser.pos;
            pos1 = LuceneParser.pos;
            if (LuceneParser.input.charCodeAt(LuceneParser.pos) === 91) {
                result0 = "[";
                LuceneParser.pos++;
            }
            else {
                result0 = null;
                if (LuceneParser.reportFailures === 0) {
                    LuceneParser.matchFailed("\"[\"");
                }
            }
            if (result0 !== null) {
                result1 = LuceneParser.parse_unquoted_term();
                if (result1 !== null) {
                    result2 = [];
                    result3 = LuceneParser.parse__();
                    while (result3 !== null) {
                        result2.push(result3);
                        result3 = LuceneParser.parse__();
                    }
                    if (result2 !== null) {
                        if (LuceneParser.input.substr(LuceneParser.pos, 2) === "TO") {
                            result3 = "TO";
                            LuceneParser.pos += 2;
                        }
                        else {
                            result3 = null;
                            if (LuceneParser.reportFailures === 0) {
                                LuceneParser.matchFailed("\"TO\"");
                            }
                        }
                        if (result3 !== null) {
                            result5 = LuceneParser.parse__();
                            if (result5 !== null) {
                                result4 = [];
                                while (result5 !== null) {
                                    result4.push(result5);
                                    result5 = LuceneParser.parse__();
                                }
                            }
                            else {
                                result4 = null;
                            }
                            if (result4 !== null) {
                                result5 = LuceneParser.parse_unquoted_term();
                                if (result5 !== null) {
                                    if (LuceneParser.input.charCodeAt(LuceneParser.pos) === 93) {
                                        result6 = "]";
                                        LuceneParser.pos++;
                                    }
                                    else {
                                        result6 = null;
                                        if (LuceneParser.reportFailures === 0) {
                                            LuceneParser.matchFailed("\"]\"");
                                        }
                                    }
                                    if (result6 !== null) {
                                        result0 = [result0, result1, result2, result3, result4, result5, result6];
                                    }
                                    else {
                                        result0 = null;
                                        LuceneParser.pos = pos1;
                                    }
                                }
                                else {
                                    result0 = null;
                                    LuceneParser.pos = pos1;
                                }
                            }
                            else {
                                result0 = null;
                                LuceneParser.pos = pos1;
                            }
                        }
                        else {
                            result0 = null;
                            LuceneParser.pos = pos1;
                        }
                    }
                    else {
                        result0 = null;
                        LuceneParser.pos = pos1;
                    }
                }
                else {
                    result0 = null;
                    LuceneParser.pos = pos1;
                }
            }
            else {
                result0 = null;
                LuceneParser.pos = pos1;
            }
            if (result0 !== null) {
                result0 = (function (offset, term_min, term_max) {
                    return {
                        'term_min': term_min,
                        'term_max': term_max,
                        'inclusive': true
                    };
                })(pos0, result0[1], result0[5]);
            }
            if (result0 === null) {
                LuceneParser.pos = pos0;
            }
            if (result0 === null) {
                pos0 = LuceneParser.pos;
                pos1 = LuceneParser.pos;
                if (LuceneParser.input.charCodeAt(LuceneParser.pos) === 123) {
                    result0 = "{";
                    LuceneParser.pos++;
                }
                else {
                    result0 = null;
                    if (LuceneParser.reportFailures === 0) {
                        LuceneParser.matchFailed("\"{\"");
                    }
                }
                if (result0 !== null) {
                    result1 = LuceneParser.parse_unquoted_term();
                    if (result1 !== null) {
                        result2 = [];
                        result3 = LuceneParser.parse__();
                        while (result3 !== null) {
                            result2.push(result3);
                            result3 = LuceneParser.parse__();
                        }
                        if (result2 !== null) {
                            if (LuceneParser.input.substr(LuceneParser.pos, 2) === "TO") {
                                result3 = "TO";
                                LuceneParser.pos += 2;
                            }
                            else {
                                result3 = null;
                                if (LuceneParser.reportFailures === 0) {
                                    LuceneParser.matchFailed("\"TO\"");
                                }
                            }
                            if (result3 !== null) {
                                result5 = LuceneParser.parse__();
                                if (result5 !== null) {
                                    result4 = [];
                                    while (result5 !== null) {
                                        result4.push(result5);
                                        result5 = LuceneParser.parse__();
                                    }
                                }
                                else {
                                    result4 = null;
                                }
                                if (result4 !== null) {
                                    result5 = LuceneParser.parse_unquoted_term();
                                    if (result5 !== null) {
                                        if (LuceneParser.input.charCodeAt(LuceneParser.pos) === 125) {
                                            result6 = "}";
                                            LuceneParser.pos++;
                                        }
                                        else {
                                            result6 = null;
                                            if (LuceneParser.reportFailures === 0) {
                                                LuceneParser.matchFailed("\"}\"");
                                            }
                                        }
                                        if (result6 !== null) {
                                            result0 = [result0, result1, result2, result3, result4, result5, result6];
                                        }
                                        else {
                                            result0 = null;
                                            LuceneParser.pos = pos1;
                                        }
                                    }
                                    else {
                                        result0 = null;
                                        LuceneParser.pos = pos1;
                                    }
                                }
                                else {
                                    result0 = null;
                                    LuceneParser.pos = pos1;
                                }
                            }
                            else {
                                result0 = null;
                                LuceneParser.pos = pos1;
                            }
                        }
                        else {
                            result0 = null;
                            LuceneParser.pos = pos1;
                        }
                    }
                    else {
                        result0 = null;
                        LuceneParser.pos = pos1;
                    }
                }
                else {
                    result0 = null;
                    LuceneParser.pos = pos1;
                }
                if (result0 !== null) {
                    result0 = (function (offset, term_min, term_max) {
                        return {
                            'term_min': term_min,
                            'term_max': term_max,
                            'inclusive': false
                        };
                    })(pos0, result0[1], result0[5]);
                }
                if (result0 === null) {
                    LuceneParser.pos = pos0;
                }
            }
            return result0;
        }
        static parse_operator_exp() {
            var result0, result1, result2, result3;
            var pos0, pos1;
            pos0 = LuceneParser.pos;
            pos1 = LuceneParser.pos;
            result0 = [];
            result1 = LuceneParser.parse__();
            while (result1 !== null) {
                result0.push(result1);
                result1 = LuceneParser.parse__();
            }
            if (result0 !== null) {
                result1 = LuceneParser.parse_operator();
                if (result1 !== null) {
                    result3 = LuceneParser.parse__();
                    if (result3 !== null) {
                        result2 = [];
                        while (result3 !== null) {
                            result2.push(result3);
                            result3 = LuceneParser.parse__();
                        }
                    }
                    else {
                        result2 = null;
                    }
                    if (result2 !== null) {
                        result0 = [result0, result1, result2];
                    }
                    else {
                        result0 = null;
                        LuceneParser.pos = pos1;
                    }
                }
                else {
                    result0 = null;
                    LuceneParser.pos = pos1;
                }
            }
            else {
                result0 = null;
                LuceneParser.pos = pos1;
            }
            if (result0 !== null) {
                result0 = (function (offset, operator) {
                    return operator;
                })(pos0, result0[1]);
            }
            if (result0 === null) {
                LuceneParser.pos = pos0;
            }
            if (result0 === null) {
                pos0 = LuceneParser.pos;
                pos1 = LuceneParser.pos;
                result0 = [];
                result1 = LuceneParser.parse__();
                while (result1 !== null) {
                    result0.push(result1);
                    result1 = LuceneParser.parse__();
                }
                if (result0 !== null) {
                    result1 = LuceneParser.parse_operator();
                    if (result1 !== null) {
                        result2 = LuceneParser.parse_EOF();
                        if (result2 !== null) {
                            result0 = [result0, result1, result2];
                        }
                        else {
                            result0 = null;
                            LuceneParser.pos = pos1;
                        }
                    }
                    else {
                        result0 = null;
                        LuceneParser.pos = pos1;
                    }
                }
                else {
                    result0 = null;
                    LuceneParser.pos = pos1;
                }
                if (result0 !== null) {
                    result0 = (function (offset, operator) {
                        return operator;
                    })(pos0, result0[1]);
                }
                if (result0 === null) {
                    LuceneParser.pos = pos0;
                }
            }
            return result0;
        }
        static parse_operator() {
            var result0;
            var pos0;
            if (LuceneParser.input.substr(LuceneParser.pos, 2) === "OR") {
                result0 = "OR";
                LuceneParser.pos += 2;
            }
            else {
                result0 = null;
                if (LuceneParser.reportFailures === 0) {
                    LuceneParser.matchFailed("\"OR\"");
                }
            }
            if (result0 === null) {
                if (LuceneParser.input.substr(LuceneParser.pos, 3) === "AND") {
                    result0 = "AND";
                    LuceneParser.pos += 3;
                }
                else {
                    result0 = null;
                    if (LuceneParser.reportFailures === 0) {
                        LuceneParser.matchFailed("\"AND\"");
                    }
                }
                if (result0 === null) {
                    if (LuceneParser.input.substr(LuceneParser.pos, 3) === "NOT") {
                        result0 = "NOT";
                        LuceneParser.pos += 3;
                    }
                    else {
                        result0 = null;
                        if (LuceneParser.reportFailures === 0) {
                            LuceneParser.matchFailed("\"NOT\"");
                        }
                    }
                    if (result0 === null) {
                        pos0 = LuceneParser.pos;
                        if (LuceneParser.input.substr(LuceneParser.pos, 2) === "||") {
                            result0 = "||";
                            LuceneParser.pos += 2;
                        }
                        else {
                            result0 = null;
                            if (LuceneParser.reportFailures === 0) {
                                LuceneParser.matchFailed("\"||\"");
                            }
                        }
                        if (result0 !== null) {
                            result0 = (function (offset) { return 'OR'; })(pos0);
                        }
                        if (result0 === null) {
                            LuceneParser.pos = pos0;
                        }
                        if (result0 === null) {
                            pos0 = LuceneParser.pos;
                            if (LuceneParser.input.substr(LuceneParser.pos, 2) === "&&") {
                                result0 = "&&";
                                LuceneParser.pos += 2;
                            }
                            else {
                                result0 = null;
                                if (LuceneParser.reportFailures === 0) {
                                    LuceneParser.matchFailed("\"&&\"");
                                }
                            }
                            if (result0 !== null) {
                                result0 = (function (offset) { return 'AND'; })(pos0);
                            }
                            if (result0 === null) {
                                LuceneParser.pos = pos0;
                            }
                        }
                    }
                }
            }
            return result0;
        }
        static parse_prefix_operator_exp() {
            var result0, result1;
            var pos0, pos1;
            pos0 = LuceneParser.pos;
            pos1 = LuceneParser.pos;
            result0 = [];
            result1 = LuceneParser.parse__();
            while (result1 !== null) {
                result0.push(result1);
                result1 = LuceneParser.parse__();
            }
            if (result0 !== null) {
                result1 = LuceneParser.parse_prefix_operator();
                if (result1 !== null) {
                    result0 = [result0, result1];
                }
                else {
                    result0 = null;
                    LuceneParser.pos = pos1;
                }
            }
            else {
                result0 = null;
                LuceneParser.pos = pos1;
            }
            if (result0 !== null) {
                result0 = (function (offset, operator) {
                    return operator;
                })(pos0, result0[1]);
            }
            if (result0 === null) {
                LuceneParser.pos = pos0;
            }
            return result0;
        }
        static parse_prefix_operator() {
            var result0;
            if (LuceneParser.input.charCodeAt(LuceneParser.pos) === 43) {
                result0 = "+";
                LuceneParser.pos++;
            }
            else {
                result0 = null;
                if (LuceneParser.reportFailures === 0) {
                    LuceneParser.matchFailed("\"+\"");
                }
            }
            if (result0 === null) {
                if (LuceneParser.input.charCodeAt(LuceneParser.pos) === 45) {
                    result0 = "-";
                    LuceneParser.pos++;
                }
                else {
                    result0 = null;
                    if (LuceneParser.reportFailures === 0) {
                        LuceneParser.matchFailed("\"-\"");
                    }
                }
            }
            return result0;
        }
        static parse__() {
            var result0, result1;
            LuceneParser.reportFailures++;
            if (/^[ \t\r\n\f]/.test(LuceneParser.input.charAt(LuceneParser.pos))) {
                result1 = LuceneParser.input.charAt(LuceneParser.pos);
                LuceneParser.pos++;
            }
            else {
                result1 = null;
                if (LuceneParser.reportFailures === 0) {
                    LuceneParser.matchFailed("[ \\t\\r\\n\\f]");
                }
            }
            if (result1 !== null) {
                result0 = [];
                while (result1 !== null) {
                    result0.push(result1);
                    if (/^[ \t\r\n\f]/.test(LuceneParser.input.charAt(LuceneParser.pos))) {
                        result1 = LuceneParser.input.charAt(LuceneParser.pos);
                        LuceneParser.pos++;
                    }
                    else {
                        result1 = null;
                        if (LuceneParser.reportFailures === 0) {
                            LuceneParser.matchFailed("[ \\t\\r\\n\\f]");
                        }
                    }
                }
            }
            else {
                result0 = null;
            }
            LuceneParser.reportFailures--;
            if (LuceneParser.reportFailures === 0 && result0 === null) {
                LuceneParser.matchFailed("whitespace");
            }
            return result0;
        }
        static parse_EOF() {
            var result0;
            var pos0;
            pos0 = LuceneParser.pos;
            LuceneParser.reportFailures++;
            if (LuceneParser.input.length > LuceneParser.pos) {
                result0 = LuceneParser.input.charAt(LuceneParser.pos);
                LuceneParser.pos++;
            }
            else {
                result0 = null;
                if (LuceneParser.reportFailures === 0) {
                    LuceneParser.matchFailed("any character");
                }
            }
            LuceneParser.reportFailures--;
            if (result0 === null) {
                result0 = "";
            }
            else {
                result0 = null;
                LuceneParser.pos = pos0;
            }
            return result0;
        }
        static cleanupExpected(expected) {
            expected.sort();
            var lastExpected = null;
            var cleanExpected = [];
            for (var i = 0; i < expected.length; i++) {
                if (expected[i] !== lastExpected) {
                    cleanExpected.push(expected[i]);
                    lastExpected = expected[i];
                }
            }
            return cleanExpected;
        }
        static computeErrorPosition() {
            /*
             * The first idea was to use |String.split| to break the input up to the
             * error position along newlines and derive the line and column from
             * there. However IE's |split| implementation is so broken that it was
             * enough to prevent it.
             */
            var line = 1;
            var column = 1;
            var seenCR = false;
            for (var i = 0; i < Math.max(LuceneParser.pos, LuceneParser.rightmostFailuresPos); i++) {
                var ch = LuceneParser.input.charAt(i);
                if (ch === "\n") {
                    if (!seenCR) {
                        line++;
                    }
                    column = 1;
                    seenCR = false;
                }
                else if (ch === "\r" || ch === "\u2028" || ch === "\u2029") {
                    line++;
                    column = 1;
                    seenCR = true;
                }
                else {
                    column++;
                    seenCR = false;
                }
            }
            return { line: line, column: column };
        }
        static padLeft(input, padding, length) {
            var result = input;
            var padLength = length - input.length;
            for (var i = 0; i < padLength; i++) {
                result = padding + result;
            }
            return result;
        }
        static escape(ch) {
            var charCode = ch.charCodeAt(0);
            var escapeChar;
            var length;
            if (charCode <= 0xFF) {
                escapeChar = 'x';
                length = 2;
            }
            else {
                escapeChar = 'u';
                length = 4;
            }
            return '\\' + escapeChar + LuceneParser.padLeft(charCode.toString(16).toUpperCase(), '0', length);
        }
    }
    lucene.LuceneParser = LuceneParser;
    /**
    * Start Rule for parsing
    */
    let StartRule;
    (function (StartRule) {
        StartRule[StartRule["Start"] = 0] = "Start";
        StartRule[StartRule["Node"] = 1] = "Node";
        StartRule[StartRule["GroupExp"] = 2] = "GroupExp";
        StartRule[StartRule["ParenExp"] = 3] = "ParenExp";
        StartRule[StartRule["FieldExp"] = 4] = "FieldExp";
        StartRule[StartRule["FieldName"] = 5] = "FieldName";
        StartRule[StartRule["Term"] = 6] = "Term";
        StartRule[StartRule["UnquotedTerm"] = 7] = "UnquotedTerm";
        StartRule[StartRule["TermChar"] = 8] = "TermChar";
        StartRule[StartRule["QuotedTerm"] = 9] = "QuotedTerm";
        StartRule[StartRule["ProximityModifier"] = 10] = "ProximityModifier";
        StartRule[StartRule["BoostModifier"] = 11] = "BoostModifier";
        StartRule[StartRule["FuzzyModifier"] = 12] = "FuzzyModifier";
        StartRule[StartRule["DecimalOrIntExp"] = 13] = "DecimalOrIntExp";
        StartRule[StartRule["DecimalExp"] = 14] = "DecimalExp";
        StartRule[StartRule["IntExp"] = 15] = "IntExp";
        StartRule[StartRule["RangeOperatorExp"] = 16] = "RangeOperatorExp";
        StartRule[StartRule["OperatorExp"] = 17] = "OperatorExp";
        StartRule[StartRule["Operator"] = 18] = "Operator";
        StartRule[StartRule["PrefixOperatorExp"] = 19] = "PrefixOperatorExp";
        StartRule[StartRule["PrefixOperator"] = 20] = "PrefixOperator";
        StartRule[StartRule["_"] = 21] = "_";
        StartRule[StartRule["EOF"] = 22] = "EOF";
    })(StartRule = lucene.StartRule || (lucene.StartRule = {}));
    /**
    * Syntax error during parsing
    */
    class LuceneParserSyntaxError {
        constructor(expected, found, offset, line, column) {
            this.expected = expected;
            this.found = found;
            this.message = this.buildMessage(expected, found);
            this.offset = offset;
            this.line = line;
            this.column = column;
        }
        buildMessage(expected, found) {
            var expectedHumanized, foundHumanized;
            switch (expected.length) {
                case 0:
                    expectedHumanized = "end of input";
                    break;
                case 1:
                    expectedHumanized = expected[0];
                    break;
                default:
                    expectedHumanized = expected.slice(0, expected.length - 1).join(", ")
                        + " or "
                        + expected[expected.length - 1];
            }
            foundHumanized = found ? LuceneParser.quote(found) : "end of input";
            return "Expected " + expectedHumanized + " but " + foundHumanized + " found.";
        }
    }
    lucene.LuceneParserSyntaxError = LuceneParserSyntaxError;
})(lucene = exports.lucene || (exports.lucene = {}));
